import { LocalDate } from 'src/core/common/domain/date/local-date';
import { Timezone } from 'src/core/common/domain/date/timezone';
import { TenantTimezone } from 'src/core/common/domain/date/tenant-timezone';
import { DateRange } from './date-range';
import { Nullable } from 'src/core/common/domain/types/types';

export type TaskDatePrimitives = {
  expected: string;
  from: string;
  to: string;
  // Optional to stay backward-compatible with task records stored before the API sent a per-task
  // zone, and with a tenant whose backend has not started sending it yet.
  timezone?: Nullable<string>;
};

export class TaskDate {
  constructor(public readonly expected: LocalDate, public readonly range: DateRange) {}

  static fromPrimitives(primitives: TaskDatePrimitives) {
    const timezone = Timezone.firstSupported(primitives.timezone, TenantTimezone.current());
    const expectedDate = LocalDate.fromString(primitives.expected, timezone).discardTime();

    return new TaskDate(expectedDate, DateRange.fromPrimitives({ from: primitives.from, to: primitives.to }, timezone));
  }

  // The zone the site keeps its clock in, and therefore the one every hour on this card is read in.
  get timezone(): string {
    return this.expected.timezone;
  }

  /**
   * Whether the card prints an hour at all. A range covering complete days shows only the day, and a
   * day has no hour that could read differently on another clock.
   */
  showsHours(): boolean {
    return !this.coversCompleteDays();
  }

  toPrimitives() {
    return {
      expected: this.expected.ymd,
      from: this.range.from.atom,
      to: this.range.to.atom,
      timezone: this.timezone,
    };
  }

  private coversCompleteDays(): boolean {
    const from = this.range.from.toFormat('HH:mm');
    const to = this.range.to.toFormat('HH:mm');

    return from === '00:00' && (to === '00:00' || to === '23:59');
  }
}
